import React, { useEffect, useState } from "react";
import { ArrowRight, Sun, ShieldCheck, Flame } from "lucide-react";
import { Button } from "@/components/ui/button";
import { brl, spaceImageUrl, API } from "@/lib/api";

const badgeMeta = {
  coberta: { icon: ShieldCheck, label: "Coberta" },
  descoberta: { icon: Sun, label: "Descoberta" },
  churrasqueira: { icon: Flame, label: "Churrasqueira" },
};

export default function SpaceCard({ space, onReserve, index = 0 }) {
  const meta = badgeMeta[space.type] || badgeMeta.coberta;
  const Icon = meta.icon;
  const isChurras = space.type === "churrasqueira";
  const priceLabel = isChurras
    ? `${brl(space.price_day)}`
    : `${brl(space.price_day)}–${brl(space.price_night)}`;

  const gallery = space.gallery || [];
  const images =
    gallery.length > 0 ? gallery.map((id) => `${API}/space-images/${id}`) : [spaceImageUrl(space)];
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (images.length <= 1) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % images.length), 3500);
    return () => clearInterval(t);
  }, [images.length]);

  return (
    <div
      data-testid={`space-card-${space.id}`}
      className="animate-fade-up flex flex-col overflow-hidden rounded-3xl border border-slate-200/80 bg-white shadow-sm transition-all hover:shadow-lg"
      style={{ animationDelay: `${index * 70}ms` }}
    >
      <div className="relative h-52 w-full overflow-hidden sm:h-56">
        {images.map((src, i) => (
          <img
            key={i}
            src={src}
            alt={space.name}
            loading="lazy"
            className="absolute inset-0 h-full w-full object-cover transition-opacity duration-700"
            style={{ opacity: i === idx ? 1 : 0 }}
          />
        ))}
        <span className="absolute left-4 top-4 z-10 inline-flex items-center gap-1.5 rounded-full bg-white/95 px-3 py-1.5 text-sm font-semibold text-[#1B7A45] shadow-sm backdrop-blur">
          <Icon className="h-4 w-4" />
          {meta.label}
        </span>
        {images.length > 1 && (
          <div className="absolute bottom-3 left-1/2 z-10 flex -translate-x-1/2 gap-1">
            {images.map((_, i) => (
              <span
                key={i}
                className={`h-1.5 rounded-full transition-all ${i === idx ? "w-4 bg-white" : "w-1.5 bg-white/60"}`}
              />
            ))}
          </div>
        )}
      </div>

      <div className="flex flex-1 flex-col p-5">
        <div className="flex items-start justify-between gap-3">
          <h3 className="font-display text-2xl font-bold tracking-tight text-slate-900">
            {space.name}
          </h3>
          <div className="text-right leading-none">
            <span className="font-display text-2xl font-black text-[#E8611A]">{priceLabel}</span>
            <span className="ml-0.5 text-sm font-medium text-slate-400">
              {isChurras ? "/período" : "/h"}
            </span>
          </div>
        </div>

        <p className="mt-2 line-clamp-2 text-sm leading-relaxed text-slate-600">
          {space.description}
        </p>

        <div className="mt-4 flex flex-wrap items-center gap-2">
          {(space.features || []).slice(0, 3).map((f) => (
            <span
              key={f}
              className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600"
            >
              {f}
            </span>
          ))}
        </div>

        <div className="mt-5 flex items-end justify-between gap-3 pt-1">
          <span className="text-xs text-slate-400">
            {isChurras ? "Por período · manhã, tarde ou noite" : "18h+ tarifa noite"}
          </span>
          <Button
            data-testid={`reserve-btn-${space.id}`}
            onClick={() => onReserve(space)}
            className="rounded-full bg-[#1B7A45] px-6 py-5 text-base font-semibold text-white shadow-sm transition-all hover:bg-[#156337] hover:shadow-md"
          >
            Reservar <ArrowRight className="ml-1.5 h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
